import { format } from 'date-fns';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { TournamentPlan } from 'modules/organizer/domain/interfaces/tournament-plan';
import { formatRublesFromMinorUnits } from 'modules/organizer/domain/utils/money';
import { formatSubscriptionPeriod } from 'modules/organizer/domain/utils/subscription-period';
import { useGetTournamentPlansQuery } from 'modules/organizer/store/billing-api';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SyncLoader } from 'react-spinners';
import UDHeaderTitle from 'ud-ui/components/header-title';
import { UDEntityRow } from 'ud-ui/components/paged-list';
import UDText from 'ud-ui/components/text';

function formatPlanDate(createdAt: string): string {
  if (!createdAt) {
    return '—';
  }

  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return format(date, 'dd.MM.yyyy');
}

function PlanFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex w-24 flex-col gap-0.5">
      <UDText type="caption-2" className="text-surfaceRomanSilver30">
        {label}
      </UDText>
      <UDText type="subhead" className="truncate">
        {value}
      </UDText>
    </div>
  );
}

const PlansListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setHeaderContent } = useMainLayout();
  const { data: plans, isLoading, isError } = useGetTournamentPlansQuery();

  useEffect(() => {
    setHeaderContent(<UDHeaderTitle>{t('plans.title')}</UDHeaderTitle>);
    return () => setHeaderContent(null);
  }, [setHeaderContent, t]);

  const renderPlan = useCallback(
    (plan: TournamentPlan, index: number) => (
      <UDEntityRow
        key={plan.id}
        index={index + 1}
        title={plan.name}
        subtitle={`${formatRublesFromMinorUnits(plan.priceInMinorUnits)} ₽ / ${formatSubscriptionPeriod(plan.period)}`}
        actions={
          <div className="flex items-center gap-6">
            <PlanFact label={t('plans.features')} value={String(plan.features.length)} />
            <PlanFact label={t('plans.createdAt')} value={formatPlanDate(plan.createdAt)} />
          </div>
        }
        onClick={() => navigate(Pages.PLANS.builders.view(plan.id))}
      />
    ),
    [navigate, t],
  );

  if (isLoading) {
    return (
      <div className="flex-center w-full px-6" style={{ height: '100%', minHeight: 'calc(100vh - 241px)' }}>
        <SyncLoader />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex-center w-full px-6 py-10">
        <UDText type="callout" className="text-surfaceRomanSilver30">
          {t('plans.error')}
        </UDText>
      </div>
    );
  }

  if (!plans?.length) {
    return (
      <div className="flex-center w-full px-6 py-10">
        <UDText type="callout" className="text-surfaceRomanSilver30">
          {t('plans.empty')}
        </UDText>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-2 px-6 py-6" role="list">
      {plans.map(renderPlan)}
    </div>
  );
};

export default PlansListPage;
